import { useMemo } from "react";

import { useFormattedDate } from "./use-formatted-date";

export function useRenderDateRange(args: {
  startDateAsString?: string | null;
  endDateAsString?: string | null;
  renderMonth?: "short" | "long";
}) {
  const { startDateAsString, endDateAsString, renderMonth } = args;
  const startDate = useMemo(
    () => (startDateAsString ? new Date(startDateAsString) : null),
    [startDateAsString]
  );
  const endDate = useMemo(
    () => (endDateAsString ? new Date(endDateAsString) : null),
    [endDateAsString]
  );

  const shortStartDate = useFormattedDate(startDate, {
    month: "short",
    day: "numeric",
  });
  const shortEndDate = useFormattedDate(endDate, {
    month: "short",
    day: "numeric",
  });

  const shortDates = useMemo(
    () => [shortStartDate, shortEndDate].filter(Boolean),
    [shortEndDate, shortStartDate]
  );

  const isSameMonth = startDate && startDate.getMonth() === endDate?.getMonth();

  const renderedMonth = useFormattedDate(startDate, {
    month: renderMonth ?? "short",
  });

  const startDay = useFormattedDate(startDate, {
    day: "numeric",
  });
  const endDay = useFormattedDate(endDate, {
    day: "numeric",
  });
  const renderedDays = [startDay, endDay].filter(Boolean).join(" - ");

  const [dateLine1, dateLine2] = isSameMonth ? [renderedDays, renderedMonth] : shortDates;

  const isSameYear = startDate && startDate.getFullYear() === endDate?.getFullYear();

  const renderedYear = useFormattedDate(startDate, {
    year: "numeric",
  });

  const shortStartYear = useFormattedDate(startDate, {
    year: "2-digit",
  });
  const shortEndYear = useFormattedDate(endDate, {
    year: "2-digit",
  });
  const shortYear = [shortStartYear, shortEndYear].filter(Boolean).join("/");
  const dateLineYear = isSameYear ? renderedYear : shortYear;

  return [dateLine1, dateLine2, dateLineYear, isSameMonth];
}
